import React, { createContext, ReactNode, useContext, useState } from "react";
import Loader from "../../widget/common/ui/Loader";
import DeactivateAccountProvider from "./DeactivateAccountProvider";
import { useDispatch } from "react-redux";
import ModalKeys from "../../const/ModalKeys";
import {
  addModal,
  removeModal,
} from "../../libraries/state_reducers/widget/ModalsReducer";
import UtilityHelper from "../../service/utilities/UtilityHelper";
import ShowToast from "../../widget/common/ui/Toast";
import AppTranslation from "../../service/app_translation/AppTranslation";

type DeactivateAccountContextType = {
  maxWords: number;
  passwordField: string;
  reasonField: string;
  submitDeleteAPI: () => void;
  showDeleteConfirmation: () => void;
  closeDeleteConfirmation: () => void;
  handleOnChange: (name: string, value: string) => void;
};

const DeactivateAccountContext = createContext<
  DeactivateAccountContextType | undefined
>(undefined);

export const DeactivateAccountContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const maxWords = 2000;

  const [passwordField, setPasswordField] = useState<string>("");
  const [reasonField, setReasonField] = useState<string>("");

  const showDeleteConfirmation = async () => {
    if (UtilityHelper.isEmpty(reasonField)) {
      ShowToast.error(AppTranslation.text("reason_cannot_be_empty"));
    } else if (UtilityHelper.countWords(reasonField) > maxWords) {
      ShowToast.error(AppTranslation.text("character_exceeded"));
    } else {
      setPasswordField("");
      dispatch(
        addModal({
          id: ModalKeys.deactivation_confirm_ask_password,
          size: "md",
        })
      );
    }
  };

  const closeDeleteConfirmation = () => {
    dispatch(removeModal(ModalKeys.deactivation_confirm_ask_password));
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === "password") {
      setPasswordField(fieldValue);
    } else if (fieldName === "reason") {
      setReasonField(fieldValue);
    }
  };

  const submitDeleteAPI = async () => {
    const reasonDom: HTMLInputElement | null = document.getElementById(
      "reason"
    ) as HTMLInputElement;

    if (
      UtilityHelper.isNotEmpty(passwordField) &&
      UtilityHelper.isNotEmpty(reasonDom.value)
    ) {
      Loader.modal();
      const statusResult: boolean =
        await DeactivateAccountProvider.deactiveProfileAPI(
          reasonDom.value,
          passwordField
        );
      Loader.modal(false);

      if (statusResult) {
        closeDeleteConfirmation();
        dispatch(
          addModal({
            id: ModalKeys.deactivation_success_alert,
            size: "md",
            hideHeader: true,
            static: true,
          })
        );
      }
    }
  };

  return (
    <DeactivateAccountContext.Provider
      value={{
        submitDeleteAPI,
        handleOnChange,
        closeDeleteConfirmation,
        showDeleteConfirmation,
        passwordField,
        reasonField,
        maxWords,
      }}
    >
      {children}
    </DeactivateAccountContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const DeactivateAccountContextImport = () => {
  const context = useContext(DeactivateAccountContext);
  if (!context) {
    throw new Error(
      "DeactivateAccountContextImport must be used within a DeactivateAccountContextProvider"
    );
  }
  return context;
};
